import React, { useState, useEffect } from 'react';
import { getProjectThumbnail } from '../utils/thumbnailService';

interface ProjectThumbnailProps {
  projectId: string;
  emojiThumbnail: string;
  className?: string;
  alt?: string;
}

const ProjectThumbnail: React.FC<ProjectThumbnailProps> = ({
  projectId,
  emojiThumbnail,
  className = '',
  alt = 'Project thumbnail'
}) => {
  const [thumbnailInfo, setThumbnailInfo] = useState<{
    type: 'image' | 'emoji';
    src: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const loadThumbnail = async () => {
      try {
        setIsLoading(true);
        const info = await getProjectThumbnail(projectId, emojiThumbnail);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setThumbnailInfo(info);
        }
      } catch (error) {
        console.error(`Error loading thumbnail for ${projectId}:`, error);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setThumbnailInfo({
            type: 'emoji',
            src: emojiThumbnail
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadThumbnail();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [projectId, emojiThumbnail]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={`projects__card-thumbnail projects__card-thumbnail--loading ${className}`}>
        <span>⏳</span>
      </div>
    );
  }

  // Show fallback if no thumbnail info
  if (!thumbnailInfo) {
    return (
      <div className={`projects__card-thumbnail projects__card-thumbnail--error ${className}`}>
        <span>{emojiThumbnail}</span>
      </div>
    );
  }

  // Render image thumbnail
  if (thumbnailInfo.type === 'image') {
    return (
      <div 
        className={`projects__card-thumbnail projects__card-thumbnail--image ${className}`}
        role="img"
        aria-label={alt}
      >
        <img
          src={thumbnailInfo.src}
          alt={alt}
          onError={() => {
            // Fallback to emoji on error
            setThumbnailInfo({
              type: 'emoji',
              src: emojiThumbnail
            });
          }}
        />
      </div>
    );
  }

  // Render emoji thumbnail
  return (
    <div className={`projects__card-thumbnail projects__card-thumbnail--emoji ${className}`}>
      <span>{thumbnailInfo.src}</span>
    </div>
  );
};

export default ProjectThumbnail; 